import Flex from 'legacyfrontend/components/Flex';
import LoadSpinner from 'legacyfrontend/components/spinner';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useNavigation } from 'legacyfrontend/hooks/useNavigation';
import { connect } from 'react-redux';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ContactIconsGroup } from '../../components/ui';
import { useColors } from 'legacyfrontend/hooks/useColors';
import { getBanner } from '../../api/services/marcaBlancaClientServices';
import Tag from '../../components/tag';
import ContactFormModal from '../../components/ContactFormModal';
import ShareButton from '../../components/ui/ShareButton';
import ImageGallery from './components/ImageGallery';
import { Seo } from 'core/components/seo';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import { buildIdSlugPath, slugify } from 'legacyfrontend/utils/slugify';
import BannerV1Detail from 'frontend/features/banners/components/BannerV1Detail';
import { isV1Banner } from 'frontend/features/banners/model/bannerModel';
const propertiesToTab = ['itinerario', 'incluye', 'hoteles', 'tarifas', 'salida', 'detalle'];

function hasVisibleContent(html) {
  if (html == null || html === '') return false;
  const stripped = String(html)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xA0;|\u00A0/g, ' ')
    .replace(/&#8203;|\u200B|\u200C|\u200D|\uFEFF/g, '')
    .trim();
  return stripped.length > 0;
}

const BannerDetail = (props) => {
  const { t } = useTranslation('layout');
  const { t: t2 } = useTranslation('translations');
  const { background: mbrBackground } = useColors();

  const location = useLocation();
  const history = useHistory();
  const { goBack } = useNavigation();
  const [bannerData, setBannerData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultaBanner, setConsultaBanner] = useState();
  const [currentTab, setCurrentTab] = useState();
  const [shareUrl, setShareUrl] = useState('');
  const encode = encodeURIComponent;
  const appName = useAppName();
  const requestIdRef = useRef(0);

  const bannerRefNumber = bannerData?.nroRef || '';

  const seoData = useMemo(() => {
    if (!bannerData) return null;
    const title = appName;
    const description = bannerData.descripcion
      ? bannerData.descripcion.length > 160
        ? bannerData.descripcion.substring(0, 157) + '...'
        : bannerData.descripcion
      : `Consultá esta oferta especial en ${appName}.`;
    return { title, description };
  }, [bannerData, appName]);

  const galleryItems = useMemo(() => {
    if (!bannerData) return [];

    const extraImages = bannerData.bannerImagenes?.length ? [...bannerData.bannerImagenes] : [];

    if (bannerData.imagen && bannerData.imageDescriptionBanner) {
      return [bannerData.imagen, ...extraImages];
    }

    return extraImages;
  }, [bannerData]);

  useEffect(() => {
    if (!props.token) {
      return undefined;
    }

    const routeBannerId = props.match.params.id;
    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    let isActive = true;

    const fetchBanner = async () => {
      try {
        setIsLoading(true);
        setBannerData(null);
        const data = await getBanner(routeBannerId, props.token);

        if (!isActive || requestIdRef.current !== currentRequestId) {
          return;
        }

        setBannerData(data);
        setError(null);
        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        setShareUrl(`${baseUrl}/bannershare/${encode(data.id)}`);
      } catch (err) {
        if (!isActive || requestIdRef.current !== currentRequestId) {
          return;
        }

        setError(err);
        history.replace('/');
      } finally {
        if (isActive && requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    };

    fetchBanner();
    return () => {
      isActive = false;
    };
  }, [props.match.params.id, encode, props.token, history]);

  useEffect(() => {
    if (!isLoading && bannerData && isV1Banner(bannerData) && !bannerData.publicado) {
      history.replace('/');
    }
  }, [bannerData, isLoading, history]);

  useEffect(() => {
    if (!bannerData || isV1Banner(bannerData)) return;

    const slugFromTitle = slugify(bannerData.titulo || '');
    const { id, slug } = props.match.params;
    const loadedBannerId = bannerData.id;

    if (!slugFromTitle || String(loadedBannerId) !== String(id)) return;

    const canonicalPath = buildIdSlugPath(ROUTE_KEYS.CONSULTA, id, bannerData.titulo);

    if (!slug || slug !== slugFromTitle) {
      history.replace(canonicalPath);
    }
  }, [bannerData, props.match.params.id, props.match.params.slug, history]);

  const translationMap = {
    itinerario: t('layout:packages.details.itinerary'),
    incluye: t('layout:packages.details.included'),
    hoteles: t('layout:packages.details.hotels'),
    tarifas: t('layout:packages.details.rates'),
    salida: t('layout:packages.details.departure'),
    detalle: t('layout:packages.details.detail'),
  };

  const translationMapInverse = {
    itinerary: 'itinerario',
    includes: 'incluye',
    hotels: 'hoteles',
    rates: 'tarifas',
    departure: 'salida',
    detail: 'detalle',
  };

  const orderSpanishArray = (englishArray, spanishArray) => {
    const orderedSpanishArray = englishArray
      .map((englishKey) => translationMapInverse[englishKey.toLowerCase()])
      .filter((key) => spanishArray.includes(key));

    return orderedSpanishArray;
  };

  const tabs = useMemo(() => {
    if (bannerData) {
      const arrayOrder = orderSpanishArray(bannerData.ordenSecciones || [], propertiesToTab);
      const tabsContent = arrayOrder
        .map((key) => {
          const content = bannerData[key];
          const translatedKey = translationMap[key.toLowerCase()] || key;
          return hasVisibleContent(content) ? [translatedKey, content] : undefined;
        })
        .filter(Boolean);

      return tabsContent?.length ? tabsContent : undefined;
    }
    return undefined;
  }, [bannerData]);

  useEffect(() => {
    if (!isLoading && bannerData && !!tabs?.length) {
      setCurrentTab(tabs[0][0]);
    }
  }, [bannerData, isLoading, tabs]);

  const shareBanner = async () => {
    navigator.clipboard.writeText(shareUrl);
    alert(t('common.link_copied'));
  };

  if (bannerData && isV1Banner(bannerData)) {
    if (!bannerData.publicado) {
      return (
        <>
          {seoData && <Seo {...seoData} />}
          <Flex
            style={{
              flex: 1,
              height: '30rem',
              justifyContent: 'center',
              padding: '8rem 0',
            }}
          >
            <LoadSpinner />
          </Flex>
        </>
      );
    }

    return (
      <>
        {seoData && <Seo {...seoData} />}
        <BannerV1Detail
          banner={bannerData}
          shareUrl={shareUrl}
          bannerRefNumber={bannerRefNumber}
          onBack={goBack}
          onEmailClick={() => {
            setConsultaBanner({
              ...bannerData,
              providerName: bannerData.proveedor || '',
              link: location.pathname,
            });
          }}
        />
        <ContactFormModal
          consultaPackage={consultaBanner}
          onClose={() => setConsultaBanner(undefined)}
          variant='banner'
          title='Enviá tu consulta'
          subtitle={consultaBanner?.titulo}
        />
      </>
    );
  }

  return (
    <>
      {seoData && <Seo {...seoData} />}
      {bannerData ? (
        <Flex className='banner-detail-container p-4 gap-8' vertical>
          <Flex className='banner__header'>
            <h3 className='text-lg flex items-center'>
              <IconButton
                onClick={goBack}
                sx={{
                  backgroundColor: 'white',
                  width: '2rem',
                  height: '2rem',
                  marginRight: { xs: 'auto', sm: '0.5rem' },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <span>{bannerData.titulo}</span>
              <ShareButton 
                url={shareUrl}
                title="Compartir banner"
                variant="banner"
                className="ml-2"
              />
            </h3>
          </Flex>
          <Flex className='boxes-wrapper'>
            <Flex className='boxes-wrapper banner-information' vertical>
              <ImageGallery
                title={bannerData.titulo}
                items={galleryItems}
              />
            </Flex>
            <Flex className='boxes-wrapper banner-details' vertical>
              <Flex className='banner-details-cards' vertical>
                <Flex className='card-box banner-details__summary-card' vertical>
                  <Flex className='banner-details__summary-card__header with-bottom-divider'>
                    <h4 className='banner-information__summary-title'>
                      {bannerData.titulo}
                    </h4>
                  </Flex>
                  {bannerRefNumber ? (
                    <div className='banner-details__ref-number'>
                      <p>Nro. Ref.: {bannerRefNumber}</p>
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(bannerRefNumber);
                          alert(t('common.link_copied'));
                        }}
                        sx={{
                          width: 24,
                          height: 24,
                          padding: '4px',
                          marginLeft: '8px',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </div>
                  ) : null}
                  <Flex
                    className='banner-details__summary-card__content'
                    vertical
                  >
                    <Flex vertical>
                      <p>
                        {bannerData.descripcion}
                      </p>
                    </Flex>
                  </Flex>
                  <Flex className='banner-details__summary-card__footer' vertical>
                    <Flex className='banner-details__tags'>
                      {[bannerData.extra, bannerData.precio, bannerData.descuento]
                        .filter(Boolean)
                        .map((item, index) => (
                          <Tag key={item + index} text={item} />
                        ))}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
              <ContactIconsGroup
                productName={bannerData.titulo}
                productRefNumber={bannerRefNumber}
                onEmailClick={() => {
                  setConsultaBanner({
                    ...bannerData,
                    providerName: bannerData.proveedor || '',
                    link: location.pathname,
                  });
                }}
              />
            </Flex>
          </Flex>
          {!!currentTab && !!tabs?.length && (
            <Flex vertical className='card-box banner-information__description-card'>
              <ul className='flex flex-wrap gap-2 items-center with-bottom-divider'>
                {tabs.map(([tabName]) => (
                  <li
                    key={tabName}
                    className={`w-36 inline-flex rounded-sm p-4 cursor-pointer uppercase leading-4 items-center justify-center transition-all duration-300${tabName === currentTab ? ' bg-[var(--mbr-background)] text-[var(--mbr-text)]' : ''}`}
                    onClick={() => setCurrentTab(tabName)}
                  >
                    {tabName}
                  </li>
                ))}
              </ul>
              <Flex
                dangerouslySetInnerHTML={{
                  __html: Object.fromEntries(tabs)[currentTab],
                }}
                vertical
                className='banner-information__description-card__inserted-html ql-editor'
                id='tabContent'
              />
            </Flex>
          )}
          <ContactFormModal
            consultaPackage={consultaBanner}
            onClose={() => setConsultaBanner(undefined)}
            variant='banner'
            title='Enviá tu consulta'
            subtitle={consultaBanner?.titulo}
          />
        </Flex>
      ) : (
        <Flex
          style={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
          }}
        >
          <LoadSpinner />
        </Flex>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  token: state.applicationState.token,
});

export default connect(mapStateToProps)(BannerDetail);
